<template>
    <v-card :loading="loading">
        <v-card-title class="text-h5">
            <v-btn
                class="mr-3"
                depressed
                color="primary"
                icon
                rounded
                @click="closeDialog"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
            {{ createDialog ? 'New Entry' : 'Edit Entry' }}
            <v-spacer></v-spacer>
        </v-card-title>
        <v-divider class="mx-5" />
        <v-card-text>
            <v-form v-model="valid" class="mx-5 mt-3">
                <v-row>
                    <v-col cols="12">
                        <v-menu
                            v-model="menu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                            :disabled="!createDialog"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="entry.date"
                                    label="Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules="[rules.required]"
                                    required
                                    hide-details
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="entry.date"
                                @input="menu = false"
                                :min="min"
                                type="month"
                                scrollable
                                :allowed-dates="allowedDates"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                </v-row>
                <v-row class="mt-0">
                    <v-col cols="12">
                        <v-text-field
                            v-model="entry.percentage"
                            type="number"
                            label="Percentage"
                            prepend-icon="mdi-percent"
                            @input="calculateAmount()"
                            :rules="[rules.number]"
                            hide-details
                        >
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row class="mt-n1"
                    ><v-col cols="12">
                        <v-text-field
                            v-model="entry.amount"
                            type="number"
                            label="Amount"
                            required
                            prepend-icon="mdi-currency-usd"
                            @input="calculatePercentage()"
                            :rules="[rules.required, maxAmount()]"
                        >
                        </v-text-field> </v-col
                ></v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
                text
                color="primary"
                :loading="loading"
                :disabled="!valid"
                @click="addEntry"
            >
                Save
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import _ from 'lodash'
import moment from 'moment'

export default {
    name: 'ForecastEntryForm',
    props: {
        createDialog: {
            type: Boolean,
            required: true,
            default: () => true,
        },
        originalEntry: {
            type: Object,
            required: true,
            default: () => {},
        },
        dates: {
            type: Array,
            required: true,
            default: () => [],
        },
        forecast: {
            type: Object,
            required: true,
            default: () => [],
        },
        scope: {
            type: Object,
            required: true,
            default: () => {},
        },
    },
    data() {
        return {
            loading: false,
            valid: false,
            rules: {
                required: v => !!v || 'Required',
                number: v => v >= 0 || 'Not valid',
            },
            entry: {},
            min: new Date().toISOString(),
            menu: false,
            isCurrentMonth: false,
        }
    },
    async mounted() {
        try {
            this.loading = true
            this.entry = _.cloneDeep(this.originalEntry)

            const forecastedAmount = this.calculateForecastedAmount()
            this.maxAmountValue =
                this.scope.totalPrice -
                this.scope.totalInvoiced -
                forecastedAmount
            this.calculateAmount()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        closeDialog() {
            this.$emit('closeDialog')
        },
        calculateForecastedAmount() {
            let currentDate = moment(new Date()).format('YYYY-MM')

            this.isCurrentMonth =
                this.entry.date && this.entry.date == currentDate

            let total = 0
            const forecastKey = Object.keys(this.forecast.scopes).find(
                key => this.forecast.scopes[key].id == this.scope.id
            )
            if (forecastKey) {
                const forecastDates = this.forecast.scopes[forecastKey].dates

                if (forecastDates) {
                    Object.values(forecastDates).forEach(date => {
                        if (date.forecast) {
                            const forecast = date.forecast
                            if (
                                moment(forecast.date).isSameOrAfter(currentDate)
                            ) {
                                total += Number(forecast.amount) || 0
                            }
                        }
                        if (date.amount) {
                            if (moment(date.date).isSameOrAfter(currentDate)) {
                                total += Number(date.amount) || 0
                            }
                        }
                    })
                }

                const currentEntry = Object.values(forecastDates).find(e => {
                    let entryDate = e.date || e.forecast?.date
                    if (!entryDate) return false
                    return moment(entryDate).isSame(this.entry.date, 'day')
                })

                if (currentEntry) {
                    total -= Number(
                        currentEntry.amount ??
                            currentEntry.forecast?.amount ??
                            0
                    )
                }
            }
            return total
        },
        addEntry() {
            this.entry.updatedOn = {
                _seconds: Math.floor(Date.now() / 1000),
                _nanoseconds: (Date.now() % 1000) * 1e6,
            }
            if (this.createDialog) {
                this.$emit('addEntry', this.entry)
            } else {
                this.$emit('replaceEntry', this.entry)
            }
            this.closeDialog()
        },
        calculateAmount() {
            this.entry.amount = (
                (Number(this.entry.percentage || 0) / 100) *
                this.scope.totalPrice
            ).toFixed(2)
        },
        calculatePercentage() {
            this.entry.percentage = (
                (Number(this.entry.amount || 0) * 100) /
                this.scope.totalPrice
            ).toFixed(3)
        },
        maxAmount() {
            const maxValue = this.maxAmountValue || 0
            if (
                Math.abs(this.entry.amount) >
                Math.abs(Number(maxValue.toFixed(2)))
            ) {
                return `The value cannot be greater than ${maxValue}.`
            } else {
                return true
            }
        },
        allowedDates(val) {
            const currentDate = moment().format('YYYY-MM')
            return (
                moment(val).isSameOrAfter(currentDate, 'month') &&
                !this.dates.includes(val)
            )
        },
        isFirstFiveDaysOfMonth() {
            const day = moment().date()
            return day <= 5
        },
    },
}
</script>
